import React, {
  KeyboardEventHandler,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { isKeyHotkey } from 'is-hotkey';
import { EventType, IContent, MsgType, RelationType, Room } from 'matrix-js-sdk';
import { ReactEditor } from 'slate-react';
import { Transforms, Editor } from 'slate';
import {
  Box,
  Dialog,
  Icon,
  IconButton,
  Icons,
  Line,
  Overlay,
  OverlayBackdrop,
  OverlayCenter,
  PopOut,
  Scroll,
  Text,
  config,
  toRem,
  Button,
} from 'folds';

import { useMatrixClient } from '../../hooks/useMatrixClient';
import {
  CustomEditor,
  Toolbar,
  toMatrixCustomHTML,
  toPlainText,
  AUTOCOMPLETE_PREFIXES,
  AutocompletePrefix,
  AutocompleteQuery,
  getAutocompleteQuery,
  getPrevWorldRange,
  resetEditor,
  RoomMentionAutocomplete,
  UserMentionAutocomplete,
  EmoticonAutocomplete,
  createEmoticonElement,
  moveCursor,
  resetEditorHistory,
  customHtmlEqualsPlainText,
  trimCustomHtml,
  isEmptyEditor,
  getBeginCommand,
  trimCommand,
} from '../../components/editor';
import { EmojiBoard, EmojiBoardTab } from '../../components/emoji-board';
import { UseStateProvider } from '../../components/UseStateProvider';
import { TUploadContent, encryptFile, getImageInfo, getMxIdLocalPart, mxcUrlToHttp } from '../../utils/matrix';
import { useTypingStatusUpdater } from '../../hooks/useTypingStatusUpdater';
import { useFilePicker } from '../../hooks/useFilePicker';
import { useFilePasteHandler } from '../../hooks/useFilePasteHandler';
import { useFileDropZone } from '../../hooks/useFileDrop';
import {
  TUploadItem,
  roomIdToMsgDraftAtomFamily,
  roomIdToReplyDraftAtomFamily,
  roomIdToUploadItemsAtomFamily,
  roomUploadAtomFamily,
} from '../../state/room/roomInputDrafts';
import { UploadCardRenderer } from '../../components/upload-card';
import {
  UploadBoard,
  UploadBoardContent,
  UploadBoardHeader,
  UploadBoardImperativeHandlers,
} from '../../components/upload-board';
import {
  Upload,
  UploadStatus,
  UploadSuccess,
  createUploadFamilyObserverAtom,
} from '../../state/upload';
import { getImageUrlBlob, loadImageElement } from '../../utils/dom';
import { safeFile } from '../../utils/mimeTypes';
import { fulfilledPromiseSettledResult } from '../../utils/common';
import { useSetting } from '../../state/hooks/settings';
import { settingsAtom } from '../../state/settings';
import {
  getAudioMsgContent,
  getFileMsgContent,
  getImageMsgContent,
  getVideoMsgContent,
} from './msgContent';
import colorMXID from '../../../util/colorMXID';
import {
  getAllParents,
  getMemberDisplayName,
  parseReplyBody,
  parseReplyFormattedBody,
  trimReplyFromBody,
  trimReplyFromFormattedBody,
} from '../../utils/room';
import { sanitizeText } from '../../utils/sanitize';
import { CommandAutocomplete } from './CommandAutocomplete';
import { Command, SHRUG, useCommands } from '../../hooks/useCommands';
import { mobileOrTablet } from '../../utils/user-agent';
import { useElementSizeObserver } from '../../hooks/useElementSizeObserver';
import { ReplyLayout, ThreadIndicator } from '../../components/message';
import { roomToParentsAtom } from '../../state/room/roomToParents';
import { useMediaAuthentication } from '../../hooks/useMediaAuthentication';
import { useRoomProcessInfo } from '../../state/room/roomProcessInfo';
import { updateRoomProcessInfo } from '../../../client/action/navigation';

import { actStrFixing, actStrSatiSurvey, actStrRepairOrder, CurveMakeADialogCmd, CurveMakeAIActionCmd } from '../../utils/CurveCmds';

import attachFileLogo from '../../../../public/icon/上傳檔案.png';
import buildConfig from '../../../../build.config';
import { WordBookButton } from '../../components/wordbook';
import { useRoomArchivedInfo } from '../../state/room/roomStateInfo';
import { useTranslation } from 'react-i18next';

interface RoomInputProps {
  editor: Editor;
  fileDropContainerRef: RefObject<HTMLElement>;
  roomId: string;
  room: Room;
}
export const RoomInput = forwardRef<HTMLDivElement, RoomInputProps>(
  ({ editor, fileDropContainerRef, roomId, room }, ref) => {
    const mx = useMatrixClient();
    const { t } = useTranslation();
    const useAuthentication = useMediaAuthentication();
    const [enterForNewline] = useSetting(settingsAtom, 'enterForNewline');
    const [isMarkdown] = useSetting(settingsAtom, 'isMarkdown');
    const commands = useCommands(mx, room);
    const emojiBtnRef = useRef<HTMLButtonElement>(null);
    const roomToParents = useAtomValue(roomToParentsAtom);

    const [msgDraft, setMsgDraft] = useAtom(roomIdToMsgDraftAtomFamily(roomId));
    const [replyDraft, setReplyDraft] = useAtom(roomIdToReplyDraftAtomFamily(roomId));
    const [uploadBoard, setUploadBoard] = useState(true);
    const [selectedFiles, setSelectedFiles] = useAtom(roomIdToUploadItemsAtomFamily(roomId));
    const uploadFamilyObserverAtom = createUploadFamilyObserverAtom(
      roomUploadAtomFamily,
      selectedFiles.map((f) => f.file)
    );
    const uploadBoardHandlers = useRef<UploadBoardImperativeHandlers>();
    const { fetchInfo, closeCase } = useRoomProcessInfo(mx, roomId);
    const { setRoomArchived } = useRoomArchivedInfo(mx, roomId);
    const [isCaseClosed, setIsCaseClosed] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
      const doFetch = async () => {
        try {
          const info = await fetchInfo();
          if (info && info.process_state === 2) {
            setIsCaseClosed(true);
          }
        } catch (error) {
          setIsCaseClosed(false);
          console.error('Failed to fetch room process info', error);
        }
      };
      doFetch();
    }, [fetchInfo]);

    const handleCloseCase = async () => {
      if (window.confirm(t('RoomInput.confirm_close_case'))) {
        const info = await fetchInfo();
        if (info && Object.keys(info).length !== 0) {
          setIsClosing(true);
          try {
            await closeCase();
            setIsCaseClosed(true);
            await setRoomArchived(true); // 將對話框設為封存狀態
            updateRoomProcessInfo(roomId);
          } catch (error) {
            setIsCaseClosed(false);
            console.error('Failed to close case', error);
          } finally {
            setIsClosing(false);
          }
        }
      }
    };

    const imagePackRooms: Room[] = useMemo(() => {
      const allParentSpaces = [roomId].concat(Array.from(getAllParents(roomToParents, roomId)));
      return allParentSpaces.reduce<Room[]>((list, rId) => {
        const r = mx.getRoom(rId);
        if (r) list.push(r);
        return list;
      }, []);
    }, [mx, roomId, roomToParents]);

    const [toolbar, setToolbar] = useSetting(settingsAtom, 'editorToolbar');
    const [autocompleteQuery, setAutocompleteQuery] =
      useState<AutocompleteQuery<AutocompletePrefix>>();

    const sendTypingStatus = useTypingStatusUpdater(mx, roomId);

    const handleFiles = useCallback(
      async (files: File[]) => {
        setUploadBoard(true);
        const safeFiles = files.map(safeFile);
        const fileItems: TUploadItem[] = [];

        if (mx.isRoomEncrypted(roomId)) {
          const encryptFiles = fulfilledPromiseSettledResult(
            await Promise.allSettled(safeFiles.map((f) => encryptFile(f)))
          );
          encryptFiles.forEach((ef) => fileItems.push(ef));
        } else {
          safeFiles.forEach((f) =>
            fileItems.push({ file: f, originalFile: f, encInfo: undefined })
          );
        }
        setSelectedFiles({
          type: 'PUT',
          item: fileItems,
        });
      },
      [setSelectedFiles, roomId, mx]
    );
    const pickFile = useFilePicker(handleFiles, true);
    const handlePaste = useFilePasteHandler(handleFiles);
    const dropZoneVisible = useFileDropZone(fileDropContainerRef, handleFiles);
    const [hideStickerBtn, setHideStickerBtn] = useState(document.body.clientWidth < 500);

    useElementSizeObserver(
      useCallback(() => document.body, []),
      useCallback((width) => setHideStickerBtn(width < 500), [])
    );

    useEffect(() => {
      Transforms.insertFragment(editor, msgDraft);
    }, [editor, msgDraft]);

    useEffect(
      () => () => {
        if (!isEmptyEditor(editor)) {
          const parsedDraft = JSON.parse(JSON.stringify(editor.children));
          setMsgDraft(parsedDraft);
        } else {
          setMsgDraft([]);
        }
        resetEditor(editor);
        resetEditorHistory(editor);
      },
      [roomId, editor, setMsgDraft]
    );

    const handleRemoveUpload = useCallback(
      (upload: TUploadContent | TUploadContent[]) => {
        const uploads = Array.isArray(upload) ? upload : [upload];
        setSelectedFiles({
          type: 'DELETE',
          item: selectedFiles.filter((f) => uploads.find((u) => u === f.file)),
        });
        uploads.forEach((u) => roomUploadAtomFamily.remove(u));
      },
      [setSelectedFiles, selectedFiles]
    );

    const handleCancelUpload = (uploads: Upload[]) => {
      uploads.forEach((upload) => {
        if (upload.status === UploadStatus.Loading) {
          mx.cancelUpload(upload.promise);
        }
      });
      handleRemoveUpload(uploads.map((upload) => upload.file));
    };

    const handleSendUpload = async (uploads: UploadSuccess[]) => {
      const contentsPromises = uploads.map(async (upload) => {
        const fileItem = selectedFiles.find((f) => f.file === upload.file);
        if (!fileItem) throw new Error('Broken upload');

        if (fileItem.file.type.startsWith('image')) {
          return getImageMsgContent(mx, fileItem, upload.mxc);
        }
        if (fileItem.file.type.startsWith('video')) {
          return getVideoMsgContent(mx, fileItem, upload.mxc);
        }
        if (fileItem.file.type.startsWith('audio')) {
          return getAudioMsgContent(fileItem, upload.mxc);
        }
        return getFileMsgContent(fileItem, upload.mxc);
      });
      handleCancelUpload(uploads);
      const contents = fulfilledPromiseSettledResult(await Promise.allSettled(contentsPromises));
      contents.forEach((content) => mx.sendMessage(roomId, content));
    };

    const submit = useCallback(() => {
      uploadBoardHandlers.current?.handleSend();

      const commandName = getBeginCommand(editor);

      let plainText = toPlainText(editor.children).trim();
      let customHtml = trimCustomHtml(
        toMatrixCustomHTML(editor.children, {
          allowTextFormatting: true,
          allowBlockMarkdown: isMarkdown,
          allowInlineMarkdown: isMarkdown,
        })
      );
      let msgType = MsgType.Text;

      if (commandName) {
        plainText = trimCommand(commandName, plainText);
        customHtml = trimCommand(commandName, customHtml);
      }
      if (commandName === Command.Me) {
        msgType = MsgType.Emote;
      } else if (commandName === Command.Notice) {
        msgType = MsgType.Notice;
      } else if (commandName === Command.Shrug) {
        plainText = `${SHRUG} ${plainText}`;
        customHtml = `${SHRUG} ${customHtml}`;
      } else if (commandName) {
        const commandContent = commands[commandName as Command];
        if (commandContent) {
          commandContent.exe(plainText);
        }
        resetEditor(editor);
        resetEditorHistory(editor);
        sendTypingStatus(false);
        return;
      }

      if (plainText === '') return;

      let body = plainText;
      let formattedBody = customHtml;
      if (replyDraft) {
        body = parseReplyBody(replyDraft.userId, trimReplyFromBody(replyDraft.body)) + body;
        formattedBody =
          parseReplyFormattedBody(
            roomId,
            replyDraft.userId,
            replyDraft.eventId,
            replyDraft.formattedBody
              ? trimReplyFromFormattedBody(replyDraft.formattedBody)
              : sanitizeText(replyDraft.body)
          ) + formattedBody;
      }

      const content: IContent = {
        msgtype: msgType,
        body,
      };
      if (replyDraft || !customHtmlEqualsPlainText(formattedBody, body)) {
        content.format = 'org.matrix.custom.html';
        content.formatted_body = formattedBody;
      }
      if (replyDraft) {
        content['m.relates_to'] = {
          'm.in_reply_to': {
            event_id: replyDraft.eventId,
          },
        };
        if (replyDraft.relation?.rel_type === RelationType.Thread) {
          content['m.relates_to'].event_id = replyDraft.relation.event_id;
          content['m.relates_to'].rel_type = RelationType.Thread;
          content['m.relates_to'].is_falling_back = false;
        }
      }
      mx.sendMessage(roomId, content);
      resetEditor(editor);
      resetEditorHistory(editor);
      setReplyDraft(undefined);
      sendTypingStatus(false);
    }, [mx, roomId, editor, replyDraft, sendTypingStatus, setReplyDraft, isMarkdown, commands]);

    const tofix = useCallback(() => {
      uploadBoardHandlers.current?.handleSend();

      const commandName = getBeginCommand(editor);

      const RepairInfo = {
        htmldoc: "<title>報修單</title> <style> table { border: 1px solid black; border-collapse: collapse; text-align: center; } th, td { border: 1px solid black; padding: 8px; } </style> </head> <body> <table> <tr> <th>欄位</th> <th>內容</th> </tr> <tr> <td>報修單號</td> <td>[[repaire_number]]</td> </tr> <tr> <td>日期</td> <td>[[date]]</td> </tr> <tr> <td>承辦單位</td> <td>[[user_unit]]</td> </tr> <tr> <td>承辦人姓名</td> <td>[[user_name]]</td> </tr> <tr> <td>分機</td> <td>[[user_extension]]</td> </tr> <tr> <td>問題類別</td> <td>[[type]]</td> </tr> <tr> <td>文號</td> <td>[[docnumber]]</td> </tr> <tr> <td>問題</td> <td>[[query]]</td> </tr> </table> </body> </html>"
      };

      let plainText = CurveMakeADialogCmd(actStrRepairOrder + JSON.stringify(RepairInfo)); // toPlainText(editor.children).trim();
      let customHtml = trimCustomHtml(
        toMatrixCustomHTML(editor.children, {
          allowTextFormatting: true,
          allowBlockMarkdown: isMarkdown,
          allowInlineMarkdown: isMarkdown,
        })
      );
      let msgType = MsgType.Text;

      if (commandName) {
        plainText = trimCommand(commandName, plainText);
        customHtml = trimCommand(commandName, customHtml);
      }
      if (commandName === Command.Me) {
        msgType = MsgType.Emote;
      } else if (commandName === Command.Notice) {
        msgType = MsgType.Notice;
      } else if (commandName === Command.Shrug) {
        plainText = `${SHRUG} ${plainText}`;
        customHtml = `${SHRUG} ${customHtml}`;
      } else if (commandName) {
        const commandContent = commands[commandName as Command];
        if (commandContent) {
          commandContent.exe(plainText);
        }
        resetEditor(editor);
        resetEditorHistory(editor);
        sendTypingStatus(false);
        return;
      }

      if (plainText === '') return;

      let body = plainText;
      let formattedBody = customHtml;
      if (replyDraft) {
        body = parseReplyBody(replyDraft.userId, trimReplyFromBody(replyDraft.body)) + body;
        formattedBody =
          parseReplyFormattedBody(
            roomId,
            replyDraft.userId,
            replyDraft.eventId,
            replyDraft.formattedBody
              ? trimReplyFromFormattedBody(replyDraft.formattedBody)
              : sanitizeText(replyDraft.body)
          ) + formattedBody;
      }

      const content: IContent = {
        msgtype: msgType,
        body,
      };
      if (replyDraft || !customHtmlEqualsPlainText(formattedBody, body)) {
        content.format = 'org.matrix.custom.html';
        content.formatted_body = formattedBody;
      }
      if (replyDraft) {
        content['m.relates_to'] = {
          'm.in_reply_to': {
            event_id: replyDraft.eventId,
          },
        };
        if (replyDraft.relation?.rel_type === RelationType.Thread) {
          content['m.relates_to'].event_id = replyDraft.relation.event_id;
          content['m.relates_to'].rel_type = RelationType.Thread;
          content['m.relates_to'].is_falling_back = false;
        }
      }
      mx.sendMessage(roomId, content);
      resetEditor(editor);
      resetEditorHistory(editor);
      setReplyDraft(undefined);
      sendTypingStatus(false);
    }, [mx, roomId, editor, replyDraft, sendTypingStatus, setReplyDraft, isMarkdown, commands]);

    const toSurvey = useCallback(() => {
      uploadBoardHandlers.current?.handleSend();

      const commandName = getBeginCommand(editor);

      let plainText = CurveMakeAIActionCmd(actStrSatiSurvey); // toPlainText(editor.children).trim();
      let customHtml = trimCustomHtml(
        toMatrixCustomHTML(editor.children, {
          allowTextFormatting: true,
          allowBlockMarkdown: isMarkdown,
          allowInlineMarkdown: isMarkdown,
        })
      );
      let msgType = MsgType.Text;

      if (commandName) {
        plainText = trimCommand(commandName, plainText);
        customHtml = trimCommand(commandName, customHtml);
      }
      if (commandName === Command.Me) {
        msgType = MsgType.Emote;
      } else if (commandName === Command.Notice) {
        msgType = MsgType.Notice;
      } else if (commandName === Command.Shrug) {
        plainText = `${SHRUG} ${plainText}`;
        customHtml = `${SHRUG} ${customHtml}`;
      } else if (commandName) {
        const commandContent = commands[commandName as Command];
        if (commandContent) {
          commandContent.exe(plainText);
        }
        resetEditor(editor);
        resetEditorHistory(editor);
        sendTypingStatus(false);
        return;
      }

      if (plainText === '') return;

      let body = plainText;
      let formattedBody = customHtml;
      if (replyDraft) {
        body = parseReplyBody(replyDraft.userId, trimReplyFromBody(replyDraft.body)) + body;
        formattedBody =
          parseReplyFormattedBody(
            roomId,
            replyDraft.userId,
            replyDraft.eventId,
            replyDraft.formattedBody
              ? trimReplyFromFormattedBody(replyDraft.formattedBody)
              : sanitizeText(replyDraft.body)
          ) + formattedBody;
      }

      const content: IContent = {
        msgtype: msgType,
        body,
      };
      if (replyDraft || !customHtmlEqualsPlainText(formattedBody, body)) {
        content.format = 'org.matrix.custom.html';
        content.formatted_body = formattedBody;
      }
      if (replyDraft) {
        content['m.relates_to'] = {
          'm.in_reply_to': {
            event_id: replyDraft.eventId,
          },
        };
        if (replyDraft.relation?.rel_type === RelationType.Thread) {
          content['m.relates_to'].event_id = replyDraft.relation.event_id;
          content['m.relates_to'].rel_type = RelationType.Thread;
          content['m.relates_to'].is_falling_back = false;
        }
      }
      mx.sendMessage(roomId, content);
      resetEditor(editor);
      resetEditorHistory(editor);
      setReplyDraft(undefined);
      sendTypingStatus(false);
    }, [mx, roomId, editor, replyDraft, sendTypingStatus, setReplyDraft, isMarkdown, commands]);

    const handleKeyDown: KeyboardEventHandler = useCallback(
      (evt) => {
        if (isKeyHotkey('mod+enter', evt) || (!enterForNewline && isKeyHotkey('enter', evt))) {
          evt.preventDefault();
          submit();
        }
        if (isKeyHotkey('escape', evt)) {
          evt.preventDefault();
          setReplyDraft(undefined);
        }
      },
      [submit, setReplyDraft, enterForNewline]
    );

    const handleKeyUp: KeyboardEventHandler = useCallback(
      (evt) => {
        if (isKeyHotkey('escape', evt)) {
          evt.preventDefault();
          return;
        }

        sendTypingStatus(!isEmptyEditor(editor));

        const prevWordRange = getPrevWorldRange(editor);
        const query = prevWordRange
          ? getAutocompleteQuery<AutocompletePrefix>(editor, prevWordRange, AUTOCOMPLETE_PREFIXES)
          : undefined;
        setAutocompleteQuery(query);
      },
      [editor, sendTypingStatus]
    );

    const handleCloseAutocomplete = useCallback(() => {
      setAutocompleteQuery(undefined);
      ReactEditor.focus(editor);
    }, [editor]);

    const handleEmoticonSelect = (key: string, shortcode: string) => {
      editor.insertNode(createEmoticonElement(key, shortcode));
      moveCursor(editor);
    };

    const handleStickerSelect = async (mxc: string, shortcode: string, label: string) => {
      const stickerUrl = mxcUrlToHttp(mx, mxc, useAuthentication);
      if (!stickerUrl) return;

      const info = await getImageInfo(
        await loadImageElement(stickerUrl),
        await getImageUrlBlob(stickerUrl)
      );

      mx.sendEvent(roomId, EventType.Sticker, {
        body: label,
        url: mxc,
        info,
      });
    };

    // 手動報修單 Dialog 狀態
    const [manualFixOpen, setManualFixOpen] = useState(false);
    const [manualFixLoading, setManualFixLoading] = useState(false);
    const [manualFixMsg, setManualFixMsg] = useState<string | null>(null);
    const [manualFixForm, setManualFixForm] = useState({
      UserId: '', // 新增 UserId 欄位
      OrderNo: '',
      Date: new Date().toISOString().slice(0, 10), // yyyy-MM-dd
      AskOrg: '',
      Dept: '',
      ContactName: '',
      Extension: '',
      ProblemType: '',
      DocNo: '',
      Description: '',
      Attachments: [] as File[],
    });

    // 開啟手動報修 Dialog
    const toManualfix = useCallback(() => {
      setManualFixMsg(null);
      setManualFixForm({
        UserId: '', // 新增 UserId 欄位
        OrderNo: '',
        Date: new Date().toISOString().slice(0, 10), // yyyy-MM-dd
        AskOrg: '',
        Dept: '',
        ContactName: '',
        Extension: '',
        ProblemType: '',
        DocNo: '',
        Description: '',
        Attachments: [],
      });
      setManualFixOpen(true);
    }, []);

    // 表單欄位變更
    const handleManualFixChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const { name, value, type } = target;
      // 防呆：確保 textarea 也能正確更新
      if (name === 'Attachments' && target instanceof HTMLInputElement) {
        setManualFixForm(f => ({ ...f, Attachments: target.files ? Array.from(target.files) : [] }));
      } else if (type === 'textarea' || name === 'Description') {
        setManualFixForm(f => ({ ...f, Description: value ?? '' }));
      } else {
        setManualFixForm(f => ({ ...f, [name]: value }));
      }
    };

    // 送出表單
    const handleManualFixSubmit = async () => {
      setManualFixLoading(true);
      setManualFixMsg(null);
      // 必填欄位檢查
      if (!manualFixForm.UserId || !manualFixForm.Date || !manualFixForm.Description) {
        setManualFixMsg(t('ManualRepair.required_fields'));
        setManualFixLoading(false);
        return;
      }
      try {
        const formData = new FormData();
        Object.entries(manualFixForm).forEach(([k, v]) => {
          if (k === 'Attachments') {
            (v as File[]).forEach(file => formData.append('Attachments', file));
          } else if (k === 'Date') {
            // 若只有 YYYY-MM-DD，補上 T00:00:00Z
            const dateStr = typeof v === 'string' && v.length === 10 ? `${v}T00:00:00Z` : v;
            formData.append('Date', String(dateStr));
          } else {
            formData.append(k, String(v));
          }
        });
        const apiUrl = `${buildConfig.RepairingServerUrl.replace(/\/$/, '')}/api/repair-orders/manual`;
        const resp = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
        });
        const data = await resp.json();
        if (resp.ok && data.success) {
          setManualFixMsg(t('ManualRepair.submit_success'));
          setManualFixOpen(false);
        } else {
          setManualFixMsg(data.message || t('ManualRepair.submit_failed'));
        }
      } catch (err) {
        setManualFixMsg(`${t('ManualRepair.submit_failed')}: ${(err instanceof Error ? err.message : String(err))}`);
      } finally {
        setManualFixLoading(false);
      }
    };

    return (
      <div ref={ref}>
        {/* 手動報修單 Dialog */}
        {manualFixOpen && (
          <Dialog variant="Primary">
            <Box direction="Column" gap="300" style={{ minWidth: 320 }}>
              <Text size="H4">{t('ManualRepair.title')}</Text>
              <input name="UserId" placeholder={t('ManualRepair.user_id')} value={manualFixForm.UserId} onChange={handleManualFixChange} />
              <input name="OrderNo" placeholder={t('ManualRepair.order_no')} value={manualFixForm.OrderNo} onChange={handleManualFixChange} />
              <input name="Date" type="date" placeholder={t('ManualRepair.date')} value={manualFixForm.Date} onChange={handleManualFixChange} />
              <input name="AskOrg" placeholder={t('ManualRepair.ask_org')} value={manualFixForm.AskOrg} onChange={handleManualFixChange} />
              <input name="Dept" placeholder={t('ManualRepair.dept')} value={manualFixForm.Dept} onChange={handleManualFixChange} />
              <input name="ContactName" placeholder={t('ManualRepair.contact_name')} value={manualFixForm.ContactName} onChange={handleManualFixChange} />
              <input name="Extension" placeholder={t('ManualRepair.extension')} value={manualFixForm.Extension} onChange={handleManualFixChange} />
              <input name="ProblemType" placeholder={t('ManualRepair.problem_type')} value={manualFixForm.ProblemType} onChange={handleManualFixChange} />
              <input name="DocNo" placeholder={t('ManualRepair.doc_no')} value={manualFixForm.DocNo} onChange={handleManualFixChange} />
              <textarea
                name="Description"
                placeholder={t('ManualRepair.description')}
                value={manualFixForm.Description}
                onChange={handleManualFixChange}
                onFocus={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              />
              <input name="Attachments" type="file" multiple onChange={handleManualFixChange} />
              {manualFixMsg && <Text color="Error">{manualFixMsg}</Text>}
              <Box gap="200">
                <Button onClick={handleManualFixSubmit} variant="Primary" disabled={manualFixLoading}>{manualFixLoading ? t('ManualRepair.submitting') : t('ManualRepair.submit')}</Button>
                <Button onClick={() => setManualFixOpen(false)} variant="Secondary">{t('Common.cancel')}</Button>
              </Box>
            </Box>
          </Dialog>
        )}
        {selectedFiles.length > 0 && (
          <UploadBoard
            header={
              <UploadBoardHeader
                open={uploadBoard}
                onToggle={() => setUploadBoard(!uploadBoard)}
                uploadFamilyObserverAtom={uploadFamilyObserverAtom}
                onSend={handleSendUpload}
                imperativeHandlerRef={uploadBoardHandlers}
                onCancel={handleCancelUpload}
              />
            }
          >
            {uploadBoard && (
              <Scroll size="300" hideTrack visibility="Hover">
                <UploadBoardContent>
                  {Array.from(selectedFiles)
                    .reverse()
                    .map((fileItem, index) => (
                      <UploadCardRenderer
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        file={fileItem.file}
                        isEncrypted={!!fileItem.encInfo}
                        uploadAtom={roomUploadAtomFamily(fileItem.file)}
                        onRemove={handleRemoveUpload}
                      />
                    ))}
                </UploadBoardContent>
              </Scroll>
            )}
          </UploadBoard>
        )}
        <Overlay
          open={dropZoneVisible}
          backdrop={<OverlayBackdrop />}
          style={{ pointerEvents: 'none' }}
        >
          <OverlayCenter>
            <Dialog variant="Primary">
              <Box
                direction="Column"
                justifyContent="Center"
                alignItems="Center"
                gap="500"
                style={{ padding: toRem(60) }}
              >
                <Icon size="600" src={Icons.File} />
                <Text size="H4" align="Center">
                  {`${t('RoomInput.drop_files_title')}「${room?.name || t('Common.close')}」`}
                </Text>
                <Text align="Center">{t('RoomInput.drop_files_desc')}</Text>
              </Box>
            </Dialog>
          </OverlayCenter>
        </Overlay>
        {autocompleteQuery?.prefix === AutocompletePrefix.RoomMention && (
          <RoomMentionAutocomplete
            roomId={roomId}
            editor={editor}
            query={autocompleteQuery}
            requestClose={handleCloseAutocomplete}
          />
        )}
        {autocompleteQuery?.prefix === AutocompletePrefix.UserMention && (
          <UserMentionAutocomplete
            room={room}
            editor={editor}
            query={autocompleteQuery}
            requestClose={handleCloseAutocomplete}
          />
        )}
        {autocompleteQuery?.prefix === AutocompletePrefix.Emoticon && (
          <EmoticonAutocomplete
            imagePackRooms={imagePackRooms}
            editor={editor}
            query={autocompleteQuery}
            requestClose={handleCloseAutocomplete}
          />
        )}
        {autocompleteQuery?.prefix === AutocompletePrefix.Command && (
          <CommandAutocomplete
            room={room}
            editor={editor}
            query={autocompleteQuery}
            requestClose={handleCloseAutocomplete}
          />
        )}
        {/* 操作列：辭庫 + 申請結案（左）、附件 + 發送（右） */}
        <Box
          direction="Row"
          alignItems="Center"
          style={{
            width: '100%',
            padding: '6px 10px',
            borderTop: '1px solid var(--curve-border, #E5E5E5)',
            gap: '8px',
            background: 'var(--curve-bg-white, #FFFFFF)',
            flexShrink: 0,
          }}
        >
          <Box direction="Row" alignItems="Center" gap="200" grow="Yes">
            <WordBookButton
              onSelect={word => {
                Transforms.insertText(editor, word);
                ReactEditor.focus(editor);
              }}
            />
            {mx.getUserId() === room.getCreator() && (
              <Button
                onClick={handleCloseCase}
                disabled={isCaseClosed || isClosing}
                variant="primary"
                size="300"
                fill="Soft"
                outlined
                style={{ whiteSpace: 'nowrap', minWidth: '88px' }}
              >
                {isCaseClosed ? t('RoomInput.case_closed') : t('RoomInput.close_case')}
              </Button>
            )}
          </Box>
          <Box direction="Row" alignItems="Center" gap="100" shrink="No">
            <IconButton
              onClick={() => pickFile('*')}
              variant="SurfaceVariant"
              size="300"
              radii="300"
            >
              <Icon src={Icons.Attachment} />
            </IconButton>
            <IconButton onClick={submit} variant="SurfaceVariant" size="300" radii="300">
              <Icon src={Icons.Send} />
            </IconButton>
          </Box>
        </Box>

        {/* 輸入框 */}
        <Box grow="Yes" style={{ width: '100%' }}>
          <CustomEditor
            editableName="RoomInput"
            editor={editor}
            placeholder="請輸入文字或拖曳圖片..."
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPaste={handlePaste}
            top={
              replyDraft && (
                <div>
                  <Box
                    alignItems="Center"
                    gap="300"
                    style={{ padding: `${config.space.S200} ${config.space.S300} 0` }}
                  >
                    <IconButton
                      onClick={() => setReplyDraft(undefined)}
                      variant="SurfaceVariant"
                      size="300"
                      radii="300"
                    >
                      <Icon src={Icons.Cross} size="50" />
                    </IconButton>
                    <Box direction="Column">
                      {replyDraft.relation?.rel_type === RelationType.Thread && <ThreadIndicator />}
                      <ReplyLayout
                        userColor={colorMXID(replyDraft.userId)}
                        username={
                          <Text size="T300" truncate>
                            <b>
                              {getMemberDisplayName(room, replyDraft.userId) ??
                                getMxIdLocalPart(replyDraft.userId) ??
                                replyDraft.userId}
                            </b>
                          </Text>
                        }
                      >
                        <Text size="T300" truncate>
                          {trimReplyFromBody(replyDraft.body)}
                        </Text>
                      </ReplyLayout>
                    </Box>
                  </Box>
                </div>
              )
            }
            bottom={
              toolbar && (
                <div>
                  <Line variant="SurfaceVariant" size="300" />
                  <Toolbar />
                </div>
              )
            }
          />
        </Box>
      </div>
    );
  }
);
